import { t } from "@lingui/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { ScrollView } from "react-native";

import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { PanelHeader } from "@whoosh/design-system/src/components/layout/PanelHeader";
import {
  Box,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { enterEditableRoundsPrivateStepAtom } from "../../atoms/actions/EnterEditableRoundsPrivateStepAtom/enterEditableRoundsPrivateStepAtom";
import {
  canMovePlayerBetweenGroups,
  movePlayerBetweenGroupsAtom,
} from "../../atoms/actions/swapPlayersBetweenGroupsAtom/swapPlayersBetweenGroupsAtom";
import { currentBookingAtom } from "../../atoms/stores";

export const MovePlayerBetweenGroupsStep = ({
  roundId,
}: {
  roundId: string;
}) => {
  const booking = useAtomValue(currentBookingAtom);
  const movePlayer = useSetAtom(movePlayerBetweenGroupsAtom);
  const returnToEditableRounds = useSetAtom(enterEditableRoundsPrivateStepAtom);
  const groupCount =
    booking?.requestGroupCount ??
    (booking?.rounds.reduce(
      (max, round) => Math.max(max, round.bookingIndex ?? -1),
      -1
    ) ?? -1) + 1;
  const availableGroups = Array.from(
    { length: groupCount },
    (_, index) => index
  ).filter((index) =>
    canMovePlayerBetweenGroups(
      booking?.rounds ?? [],
      roundId,
      index,
      booking?.groupCapacity
    )
  );

  return (
    <VStack
      flex={1}
      backgroundColor="surface-secondary"
      testID="move-player-between-groups-step"
    >
      <PanelHeader
        paddingVariant="medium"
        header={t`Move to another group`}
        outerLeftComponent={
          <Button
            accessibilityLabel={t`Back`}
            testID="swap-players-between-groups-back-button"
            variant="secondary"
            onPress={() => returnToEditableRounds()}
            leftIcon={<ArrowLeftIcon />}
          />
        }
      />
      <ScrollView>
        <VStack gap={1} paddingHorizontal={5} paddingBottom={5}>
          {availableGroups.map((groupIndex) => (
            <Pressable
              key={groupIndex}
              testID={`move-player-to-group-${groupIndex}`}
              onPress={() => {
                movePlayer({
                  sourceRoundId: roundId,
                  targetBookingIndex: groupIndex,
                });
                returnToEditableRounds();
              }}
            >
              <Box
                padding={4}
                backgroundColor="surface-primary"
                borderRadius="2"
              >
                <Text variant="mainBaseSemiBold">{t`Move to Group ${
                  groupIndex + 1
                }`}</Text>
                <Text
                  variant="mainTiny"
                  color="text-secondary"
                >{t`Add this player to Group ${groupIndex + 1}`}</Text>
              </Box>
            </Pressable>
          ))}
          {availableGroups.length === 0 ? (
            <Text color="text-secondary">{t`No other groups are available`}</Text>
          ) : null}
        </VStack>
      </ScrollView>
    </VStack>
  );
};
